(() => {
  const STORAGE_KEY = 'adawat_compare_v1';
  const MAX_ITEMS = 3;

  const read = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    document.dispatchEvent(new CustomEvent('adawat:compare:change', { detail: { items: read() } }));
  };

  const normalize = (item) => ({
    id: String(item.id || ''),
    handle: item.handle || '',
    title: item.title || '',
    price: item.price || '',
    image: item.image || '',
    url: item.url || '#',
    vendor: item.vendor || '',
    type: item.type || '',
    available: item.available || '',
    variantId: String(item.variantId || ''),
  });

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function moneyFromCents(cents) {
    if (typeof cents !== 'number') return '';
    if (typeof window.adawatFormatMoney === 'function') return window.adawatFormatMoney(cents);
    return `$${(cents / 100).toFixed(2)}`;
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  const AdawatCompare = {
    max: MAX_ITEMS,

    get() {
      return read();
    },

    count() {
      return read().length;
    },

    has(id) {
      const key = String(id || '');
      return read().some((item) => item.id === key);
    },

    add(item) {
      const next = normalize(item);
      if (!next.id) return { items: read(), added: false, full: false };
      if (this.has(next.id)) return { items: read(), added: false, full: false };

      const items = read();
      if (items.length >= MAX_ITEMS) {
        return { items, added: false, full: true };
      }

      items.push(next);
      write(items);
      return { items, added: true, full: false };
    },

    remove(id) {
      const key = String(id || '');
      const items = read().filter((entry) => entry.id !== key);
      write(items);
      return items;
    },

    clear() {
      write([]);
      return [];
    },

    pageUrl() {
      const popup = document.querySelector('[data-adawat-compare-popup]');
      return popup?.dataset.comparePageUrl || '/search?view=compare';
    },

    openPopup() {
      const popup = document.querySelector('[data-adawat-compare-popup]');
      if (!popup) return;
      this.renderPopup();
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('adawat-compare-open');
    },

    closePopup() {
      const popup = document.querySelector('[data-adawat-compare-popup]');
      if (!popup) return;
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('adawat-compare-open');
    },

    renderPopup() {
      const listHost = document.querySelector('[data-compare-popup-list]');
      const emptyEl = document.querySelector('[data-compare-popup-empty]');
      const cta = document.querySelector('[data-compare-go]');
      if (!listHost) return;

      const items = read();
      if (cta) {
        cta.classList.toggle('is-disabled', items.length < 1);
        cta.setAttribute('aria-disabled', items.length < 1 ? 'true' : 'false');
      }

      listHost.querySelectorAll('[data-compare-popup-item]').forEach((node) => node.remove());

      if (items.length === 0) {
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      if (emptyEl) emptyEl.hidden = true;

      items.forEach((item) => {
        const title = escapeHtml(item.title);
        const image = escapeHtml(item.image || '');
        const id = escapeHtml(item.id);
        const row = document.createElement('div');
        row.className = 'adawat-compare-popup__item';
        row.setAttribute('data-compare-popup-item', '');
        row.innerHTML = `
          <div class="adawat-compare-popup__thumb">
            ${image ? `<img src="${image}" alt="${title}" width="56" height="56" loading="lazy">` : ''}
          </div>
          <p class="adawat-compare-popup__name">${title}</p>
          <button
            type="button"
            class="adawat-compare-popup__remove"
            data-compare-remove
            data-product-id="${id}"
            aria-label="Remove ${title}"
          >×</button>
        `;
        listHost.appendChild(row);
      });
    },

    optionLabel(option) {
      if (option == null) return '';
      if (typeof option === 'string') return option;
      if (typeof option === 'object') return option.name || option.value || '';
      return String(option);
    },

    async renderPage() {
      const root = document.querySelector('[data-adawat-compare-page]');
      if (!root) return;

      const empty = root.querySelector('[data-compare-page-empty]');
      const tableHost = root.querySelector('[data-compare-page-table]');
      const clearBtn = root.querySelector('[data-compare-clear-all]');
      // Only the products the shopper explicitly added — never recommendations.
      const items = read();

      if (!tableHost) return;

      if (clearBtn) clearBtn.hidden = items.length === 0;

      if (items.length === 0) {
        if (empty) empty.hidden = false;
        tableHost.innerHTML = '';
        return;
      }

      if (empty) empty.hidden = true;
      tableHost.innerHTML = `<div class="adawat-compare-page__loading">Loading comparison…</div>`;

      const enriched = await Promise.all(
        items.map(async (item) => {
          const product = item.handle ? await fetchProduct(item.handle) : null;
          if (!product) {
            return {
              ...item,
              optionValues: {},
              variantId: '',
            };
          }

          const variant = product.variants?.[0];
          const optionValues = {};
          (product.options || []).forEach((option, index) => {
            const label = this.optionLabel(option).trim();
            if (!label) return;
            if (/^title$/i.test(label)) return;
            const key = `option${index + 1}`;
            const value = variant?.[key];
            if (!value || /^default title$/i.test(String(value))) return;
            optionValues[label] = value;
          });

          let image = item.image || '';
          if (!image) {
            const featured = product.featured_image;
            image =
              (typeof featured === 'string' ? featured : featured?.src) ||
              (typeof product.images?.[0] === 'string' ? product.images[0] : product.images?.[0]?.src) ||
              '';
          }

          const price =
            item.price ||
            moneyFromCents(variant?.price != null ? variant.price : product.price) ||
            '—';

          return {
            id: item.id,
            handle: item.handle,
            url: item.url || `/products/${item.handle}`,
            title: product.title || item.title,
            price,
            image,
            vendor: product.vendor || item.vendor || '—',
            type: product.type || item.type || '—',
            available: product.available ? 'In stock' : 'Sold out',
            optionValues,
            variantId: variant?.id || item.variantId || '',
          };
        })
      );

      const optionKeys = [];
      enriched.forEach((entry) => {
        Object.keys(entry.optionValues || {}).forEach((key) => {
          if (!optionKeys.includes(key)) optionKeys.push(key);
        });
      });

      const rows = [
        {
          label: 'Brand',
          cells: enriched.map((item) => escapeHtml(item.vendor || '—')),
        },
        {
          label: 'Category',
          cells: enriched.map((item) => escapeHtml(item.type || '—')),
        },
        {
          label: 'Availability',
          cells: enriched.map((item) => {
            const stock = item.available === 'In stock';
            return `<span class="adawat-compare-page__stock ${stock ? 'is-in' : 'is-out'}">${escapeHtml(item.available || '—')}</span>`;
          }),
        },
      ];

      optionKeys.forEach((key) => {
        rows.push({
          label: key,
          cells: enriched.map((item) => escapeHtml((item.optionValues || {})[key] || '—')),
        });
      });

      const productHeads = enriched
        .map((item) => {
          const id = escapeHtml(item.id);
          const title = escapeHtml(item.title);
          const image = escapeHtml(item.image || '');
          const url = escapeHtml(item.url || '#');
          const price = escapeHtml(item.price || '—');
          const variantId = escapeHtml(item.variantId || '');
          const addDisabled = !item.variantId || item.available === 'Sold out';
          const type = escapeHtml((item.type || '').toUpperCase());

          return `
            <th scope="col" class="adawat-compare-page__col">
              <article class="adawat-compare-page__card">
                <div class="adawat-compare-page__media">
                  <button type="button" class="adawat-compare-page__remove" data-compare-remove data-product-id="${id}" aria-label="Remove ${title}">×</button>
                  <a href="${url}" class="adawat-compare-page__media-link">
                    ${image ? `<img src="${image}" alt="${title}" width="400" height="400" loading="lazy">` : ''}
                  </a>
                </div>
                ${type ? `<p class="adawat-compare-page__type">${type}</p>` : ''}
                <h3 class="adawat-compare-page__product-title">
                  <a href="${url}">${title}</a>
                </h3>
                <p class="adawat-compare-page__price">${price}</p>
                <form method="post" action="/cart/add" class="adawat-compare-page__add-form">
                  <input type="hidden" name="id" value="${variantId}">
                  <button type="submit" class="adawat-compare-page__add" ${addDisabled ? 'disabled' : ''}>
                    ${addDisabled ? 'Sold out' : '<span class="adawat-compare-page__add-full">Add to cart</span><span class="adawat-compare-page__add-short">Add</span>'}
                  </button>
                </form>
              </article>
            </th>
          `;
        })
        .join('');

      const body = rows
        .map(
          (row) => `
            <tr>
              <th scope="row" class="adawat-compare-page__row-label">${escapeHtml(row.label)}</th>
              ${row.cells.map((cell) => `<td>${cell}</td>`).join('')}
            </tr>
          `
        )
        .join('');

      const mobileCards = enriched
        .map((item, index) => {
          const id = escapeHtml(item.id);
          const title = escapeHtml(item.title);
          const image = escapeHtml(item.image || '');
          const url = escapeHtml(item.url || '#');
          const price = escapeHtml(item.price || '—');
          const variantId = escapeHtml(item.variantId || '');
          const addDisabled = !item.variantId || item.available === 'Sold out';
          const specs = rows
            .map(
              (row) => `
                <div class="adawat-compare-page__mobile-spec">
                  <span class="adawat-compare-page__mobile-spec-label">${escapeHtml(row.label)}</span>
                  <span class="adawat-compare-page__mobile-spec-value">${row.cells[index]}</span>
                </div>
              `
            )
            .join('');

          return `
            <article class="adawat-compare-page__mobile-card">
              <div class="adawat-compare-page__media">
                <button type="button" class="adawat-compare-page__remove" data-compare-remove data-product-id="${id}" aria-label="Remove ${title}">×</button>
                <a href="${url}" class="adawat-compare-page__media-link">
                  ${image ? `<img src="${image}" alt="${title}" width="600" height="600" loading="lazy">` : ''}
                </a>
              </div>
              <div class="adawat-compare-page__mobile-body">
                <h3 class="adawat-compare-page__product-title">
                  <a href="${url}">${title}</a>
                </h3>
                <p class="adawat-compare-page__price">${price}</p>
                <form method="post" action="/cart/add" class="adawat-compare-page__add-form">
                  <input type="hidden" name="id" value="${variantId}">
                  <button type="submit" class="adawat-compare-page__add" ${addDisabled ? 'disabled' : ''}>
                    ${addDisabled ? 'Sold out' : 'Add to cart'}
                  </button>
                </form>
                <div class="adawat-compare-page__mobile-specs">
                  ${specs}
                </div>
              </div>
            </article>
          `;
        })
        .join('');

      tableHost.innerHTML = `
        <div class="adawat-compare-page__desktop">
          <div class="adawat-compare-page__scroll">
            <table class="adawat-compare-page__table">
              <thead>
                <tr>
                  <th scope="col" class="adawat-compare-page__label-col"></th>
                  ${productHeads}
                </tr>
              </thead>
              <tbody>
                ${body}
              </tbody>
            </table>
          </div>
        </div>
        <div class="adawat-compare-page__mobile">
          ${mobileCards}
        </div>
      `;
    },
  };

  function productFromButton(button) {
    return {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      price: button.dataset.productPrice,
      image: button.dataset.productImage,
      url: button.dataset.productUrl,
      vendor: button.dataset.productVendor,
      type: button.dataset.productType,
      available: button.dataset.productAvailable,
      variantId: button.dataset.variantId || '',
    };
  }

  function syncHeaderCount() {
    const count = AdawatCompare.count();
    document.querySelectorAll('[data-compare-count]').forEach((el) => {
      el.textContent = String(count);
      el.dataset.count = String(count);
      if (count < 1) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
  }

  function syncButtons() {
    document.querySelectorAll('[data-compare-toggle]').forEach((button) => {
      const active = AdawatCompare.has(button.dataset.productId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        active
          ? `Remove ${button.dataset.productTitle || 'product'} from compare`
          : `Compare ${button.dataset.productTitle || 'product'}`
      );
    });
  }

  function refresh() {
    syncButtons();
    syncHeaderCount();
    const popup = document.querySelector('[data-adawat-compare-popup]');
    if (popup?.classList.contains('is-open')) AdawatCompare.renderPopup();
    if (document.querySelector('[data-adawat-compare-page]')) AdawatCompare.renderPage();
  }

  AdawatCompare.sync = () => {
    syncButtons();
    syncHeaderCount();
  };

  function flashFull(button) {
    button.classList.add('is-full');
    window.setTimeout(() => button.classList.remove('is-full'), 700);
  }

  function bind() {
    if (window.__adawatCompareBound) return;
    window.__adawatCompareBound = true;

    document.addEventListener(
      'click',
      (event) => {
      const openBtn = event.target.closest('[data-compare-open]');
      if (openBtn) {
        event.preventDefault();
        event.stopPropagation();
        AdawatCompare.openPopup();
        return;
      }

      const toggleBtn = event.target.closest('[data-compare-toggle]');
      if (toggleBtn) {
        event.preventDefault();
        event.stopPropagation();
        const product = productFromButton(toggleBtn);
        if (AdawatCompare.has(product.id)) {
          AdawatCompare.remove(product.id);
          refresh();
          return;
        }
        const result = AdawatCompare.add(product);
        if (result.full) {
          flashFull(toggleBtn);
          AdawatCompare.openPopup();
          return;
        }
        refresh();
        AdawatCompare.openPopup();
        return;
      }

      const goBtn = event.target.closest('[data-compare-go]');
      if (goBtn) {
        if (AdawatCompare.count() < 1) {
          event.preventDefault();
          return;
        }
        AdawatCompare.closePopup();
        return;
      }

      const closeBtn = event.target.closest('[data-compare-close]');
      if (closeBtn) {
        event.preventDefault();
        AdawatCompare.closePopup();
        return;
      }

      const clearAllBtn = event.target.closest('[data-compare-clear-all]');
      if (clearAllBtn) {
        event.preventDefault();
        AdawatCompare.clear();
        AdawatCompare.closePopup();
        refresh();
        return;
      }

      const removeBtn = event.target.closest('[data-compare-remove]');
      if (removeBtn) {
        event.preventDefault();
        AdawatCompare.remove(removeBtn.dataset.productId);
        refresh();
      }
    },
      { capture: true }
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') AdawatCompare.closePopup();
    });

    document.addEventListener('adawat:compare:change', () => {
      syncButtons();
      syncHeaderCount();
      const popup = document.querySelector('[data-adawat-compare-popup]');
      if (popup?.classList.contains('is-open')) AdawatCompare.renderPopup();
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.adawat-compare-page__add-form');
      if (!form || !form.closest('[data-adawat-compare-page]')) return;
      event.preventDefault();
      const button = form.querySelector('.adawat-compare-page__add');
      const id = Number(form.querySelector('[name="id"]')?.value || 0);
      if (!id || button?.disabled) return;
      const original = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        if (window.AdawatCart?.add) {
          await window.AdawatCart.add({ id, quantity: 1 });
        } else {
          const cartRoot = `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
          const res = await fetch(`${cartRoot}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id, quantity: 1 }),
          });
          if (!res.ok) throw new Error('Add failed');
          document.dispatchEvent(new CustomEvent('cart:update'));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          window.AdawatCart?.open?.();
        }
        button.textContent = 'Added';
        window.setTimeout(() => {
          button.disabled = false;
          button.innerHTML = original;
        }, 1200);
      } catch (_) {
        button.disabled = false;
        button.innerHTML = original;
      }
    });
  }

  window.AdawatCompare = AdawatCompare;

  const start = () => {
    bind();
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
